import type { Flight } from '@/lib/api/types';
import { cn } from '@/lib/utils';
import { AlertTriangle, Clock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { formatNum } from './columns';

const DASH = '—';

// Labels are the mockup's verbatim (fsl-mocks/fsl-rtm-map-mu.html, the
// Flights context added in 5c5b6b8) — including the 'Alt ' prefix on the
// three altitude columns.
const TRACK_HEADERS = [
  'Time',
  'Lat',
  'Lon',
  'Alt WGS84',
  'Alt ASL',
  'Alt AGL',
  'Speed',
  'Hdg',
  'Mode',
  'Batt',
  'RSSI',
];

/** A real flight is ~10k points at 4 Hz, and every row is 11 un-virtualised
 *  cells — rendering the whole track at once locks the pane for seconds on
 *  open. Paging keeps the DOM bounded without hiding any of the track: every
 *  point is reachable, unlike a hard cap which silently drops the tail. */
const PAGE_SIZE = 500;

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[0.846em] font-bold">Track Data</div>
      {children}
    </div>
  );
}

export function FlightTrackPanel({
  flight,
  scrubIndex,
  onScrub,
  loading = false,
  error = null,
}: {
  flight: Flight;
  scrubIndex: number;
  onScrub: (i: number) => void;
  loading?: boolean;
  error?: Error | null;
}) {
  const rowsRef = useRef(new Map<number, HTMLTableRowElement>());
  const [page, setPage] = useState(0);

  // The scrubber and the altitude profile move the cursor across the WHOLE
  // track, so the table follows it onto whichever page holds it. Paging by
  // hand does not move the cursor, so this does not fight manual navigation.
  useEffect(() => {
    setPage(Math.floor(scrubIndex / PAGE_SIZE));
  }, [scrubIndex]);

  // Keep the highlighted row visible.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `page` is load-bearing, not redundant. When the cursor moves to another page the row map is rebuilt in a later commit, so the scroll must re-run once those rows exist — the effect body reads a ref, which the rule cannot see.
  useEffect(() => {
    rowsRef.current.get(scrubIndex)?.scrollIntoView({ block: 'nearest' });
  }, [scrubIndex, page]);

  if (loading) {
    return (
      <Panel>
        <div className="rounded-md border border-(--color-border) bg-(--color-bg) p-3 text-[0.846em] text-(--color-text3)">
          Loading telemetry history…
        </div>
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel>
        <div className="flex gap-2.5 rounded-md border border-(--color-red)/40 bg-(--color-red-bg) p-3">
          <AlertTriangle size={16} className="text-(--color-red) shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div className="text-[0.846em] font-bold text-(--color-red)">
              Telemetry history unavailable
            </div>
            <p className="text-[0.769em] text-(--color-text2) leading-relaxed font-mono">
              {error.message}
            </p>
          </div>
        </div>
      </Panel>
    );
  }

  // null means the track was never loaded for this flight — the query is
  // disabled (demo, or a planned flight) or has not run. [] means it ran and
  // the flight genuinely has no points.
  if (flight.track === null) {
    return (
      <Panel>
        <div className="flex gap-2.5 rounded-md border border-(--color-amber)/40 bg-(--color-amber-bg) p-3">
          <Clock size={16} className="text-(--color-amber) shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div className="text-[0.846em] font-bold text-(--color-amber)">
              No telemetry history loaded
            </div>
            <p className="text-[0.769em] text-(--color-text2) leading-relaxed">
              The map shows the planned waypoints, the operational volume and the last known
              position.
            </p>
          </div>
        </div>
      </Panel>
    );
  }

  if (flight.track.length === 0) {
    return (
      <Panel>
        <div className="rounded-md border border-(--color-border) bg-(--color-bg) p-3 text-[0.846em] text-(--color-text3)">
          {flight.source === 'planned'
            ? 'This flight is planned and has not flown yet, so no telemetry track is available.'
            : 'The telemetry endpoint returned no points for this flight in the queried window.'}
        </div>
      </Panel>
    );
  }

  const loaded = flight.track.length;
  const pageCount = Math.max(1, Math.ceil(loaded / PAGE_SIZE));
  // Clamped rather than trusted: a shorter track can arrive while a later
  // page is open (a refetch, or the cursor reset racing the new flight).
  const current = Math.min(page, pageCount - 1);
  const start = current * PAGE_SIZE;
  const rows = flight.track.slice(start, start + PAGE_SIZE);
  // The endpoint's own count, which can exceed what it actually returned —
  // that disagreement is a backend fact, so both numbers are shown.
  const total = flight.data_point_count ?? loaded;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-[0.846em] font-bold">
          Track Data ({total.toLocaleString()} points
          {total !== loaded ? `, ${loaded.toLocaleString()} returned` : ''})
        </span>
        <span className="text-[0.692em] text-(--color-text3) font-mono">
          {(start + 1).toLocaleString()}–{(start + rows.length).toLocaleString()}
        </span>
      </div>
      <div className="max-h-[340px] overflow-auto rounded-md border border-(--color-border)">
        <table className="w-full text-[0.692em] font-mono border-collapse">
          <thead className="sticky top-0 bg-(--color-surface)">
            <tr className="border-b border-(--color-border) text-(--color-text3)">
              {TRACK_HEADERS.map((h) => (
                <th key={h} className="px-1.5 py-1 text-left whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => {
              // Cursor indices are track-wide, so every row identifies itself
              // by its absolute position, not its offset within the page.
              const idx = start + i;
              return (
                <tr
                  key={idx}
                  ref={(el) => {
                    if (el) rowsRef.current.set(idx, el);
                    else rowsRef.current.delete(idx);
                  }}
                  tabIndex={0}
                  onClick={() => onScrub(idx)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onScrub(idx);
                    }
                  }}
                  className={cn(
                    'cursor-pointer',
                    idx === scrubIndex ? 'bg-(--color-green-bg)' : 'hover:bg-(--color-bg)',
                  )}
                >
                  <td className="px-1.5 py-0.5">{p.t ? p.t.slice(11, 19) : DASH}</td>
                  <td className="px-1.5 py-0.5">{formatNum(p.lat, 5)}</td>
                  <td className="px-1.5 py-0.5">{formatNum(p.lon, 5)}</td>
                  <td className="px-1.5 py-0.5">{formatNum(p.alt_wgs84, 1)}</td>
                  <td className="px-1.5 py-0.5">{formatNum(p.alt_asl, 1)}</td>
                  <td className="px-1.5 py-0.5">{formatNum(p.alt_agl, 1)}</td>
                  <td className="px-1.5 py-0.5">{formatNum(p.speed_ms, 1)}</td>
                  <td className="px-1.5 py-0.5">{formatNum(p.heading, 0, '°')}</td>
                  <td className="px-1.5 py-0.5">{p.flight_mode ?? DASH}</td>
                  <td className="px-1.5 py-0.5">{formatNum(p.battery_pct, 0, '%')}</td>
                  <td className="px-1.5 py-0.5">{formatNum(p.rssi_dbm, 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && <Pager page={current} pageCount={pageCount} onPage={setPage} />}
    </div>
  );
}

/** Page controls. Stepping a page does NOT move the scrub cursor — the map
 *  marker and the altitude profile stay where the operator put them while
 *  they read another part of the table. */
function Pager({
  page,
  pageCount,
  onPage,
}: {
  page: number;
  pageCount: number;
  onPage: (p: number) => void;
}) {
  const go = (p: number) => onPage(Math.min(pageCount - 1, Math.max(0, p)));
  const btn =
    'h-6 min-w-6 px-2 rounded-md border border-(--color-border) text-[0.692em] disabled:opacity-40 enabled:hover:bg-(--color-bg)';

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        className={btn}
        onClick={() => go(0)}
        disabled={page === 0}
        aria-label="First page"
      >
        «
      </button>
      <button
        type="button"
        className={btn}
        onClick={() => go(page - 1)}
        disabled={page === 0}
        aria-label="Previous page"
      >
        ‹
      </button>
      <span className="text-[0.692em] text-(--color-text2) font-mono px-1">
        Page {page + 1} of {pageCount}
      </span>
      <button
        type="button"
        className={btn}
        onClick={() => go(page + 1)}
        disabled={page >= pageCount - 1}
        aria-label="Next page"
      >
        ›
      </button>
      <button
        type="button"
        className={btn}
        onClick={() => go(pageCount - 1)}
        disabled={page >= pageCount - 1}
        aria-label="Last page"
      >
        »
      </button>
    </div>
  );
}
